import { normalizeCrmActionHref } from "./safe-action-hrefs";

export type CrmContactActionTone = "neutral" | "primary" | "success" | "warning" | "danger";

export interface CrmContactAction {
  key: string;
  label: string;
  description: string | null;
  href: string;
  tone: CrmContactActionTone;
  disabled: boolean;
  disabledReason: string | null;
}

export interface CrmContactActionGroup {
  key: string;
  label: string;
  description: string | null;
  source: "configured" | "contact";
  actions: CrmContactAction[];
}

export interface BuildCrmContactActionGroupsOptions {
  actionGroupKeys?: readonly string[];
  maxGroups?: number;
  maxActionsPerGroup?: number;
}

export interface BuildCrmContactChannelActionsOptions {
  channelKeys?: readonly string[];
  maxActions?: number;
}

export const CRM_CONTACT_ACTION_GROUP_FIELD_KEYS = [
  "crmActionGroups",
  "contactActionGroups",
  "profileActionGroups",
  "profileActions",
] as const;

export const CRM_CONTACT_CHANNEL_FIELD_KEYS = [
  "crmContactChannels",
  "contactChannels",
  "communicationChannels",
  "socialChannels",
  "socialMedia",
] as const;

const PRIVATE_KEY_PATTERN = /(?:password|secret|token|authorization|credential|private[_-]?key|api[_-]?key)/i;
const TELEGRAM_HANDLE_RE = /^[A-Za-z0-9_]{5,32}$/;
const SOCIAL_HANDLE_RE = /^[A-Za-z0-9._-]{1,80}$/;
const CHANNEL_KIND_ALIASES: Record<string, CrmContactChannelKind> = {
  call: "phone",
  email: "email",
  facebook: "facebook",
  fb: "facebook",
  homepage: "website",
  instagram: "instagram",
  insta: "instagram",
  linkedin: "linkedin",
  mail: "email",
  phone: "phone",
  site: "website",
  sms: "sms",
  telegram: "telegram",
  text: "sms",
  tel: "phone",
  twitter: "x",
  url: "website",
  website: "website",
  whatsapp: "whatsapp",
  x: "x",
};
type CrmContactChannelKind =
  | "email"
  | "phone"
  | "sms"
  | "whatsapp"
  | "telegram"
  | "linkedin"
  | "website"
  | "instagram"
  | "facebook"
  | "x"
  | "other";

export function buildCrmContactActionGroups(
  customFields: unknown,
  options: BuildCrmContactActionGroupsOptions = {},
): CrmContactActionGroup[] {
  const fields = asRecord(customFields);
  if (!fields) return [];

  const actionGroupKeys = options.actionGroupKeys?.length
    ? [...options.actionGroupKeys]
    : [...CRM_CONTACT_ACTION_GROUP_FIELD_KEYS];
  const maxGroups = normalizePositiveInteger(options.maxGroups, 6);
  const maxActionsPerGroup = normalizePositiveInteger(options.maxActionsPerGroup, 8);

  return actionGroupKeys
    .flatMap((key) => parseConfiguredActionGroups(key, fields[key], maxActionsPerGroup))
    .filter((group) => group.actions.length > 0)
    .slice(0, maxGroups);
}

export function buildCrmContactChannelActions(
  customFields: unknown,
  options: BuildCrmContactChannelActionsOptions = {},
): CrmContactAction[] {
  const fields = asRecord(customFields);
  if (!fields) return [];

  const channelKeys = options.channelKeys?.length
    ? [...options.channelKeys]
    : [...CRM_CONTACT_CHANNEL_FIELD_KEYS];
  const maxActions = normalizePositiveInteger(options.maxActions, 12);
  const actions = channelKeys.flatMap((key) => parseConfiguredChannels(key, fields[key]));
  return uniqueActions(actions).slice(0, maxActions);
}

function parseConfiguredActionGroups(
  rootKey: string,
  value: unknown,
  maxActionsPerGroup: number,
): CrmContactActionGroup[] {
  if (Array.isArray(value)) {
    return value
      .map((entry, index) => parseActionGroup(`${rootKey}.${index}`, entry, maxActionsPerGroup))
      .filter((group): group is CrmContactActionGroup => Boolean(group));
  }

  const record = asRecord(value);
  if (!record) return [];

  if (hasGroupShape(record)) {
    const group = parseActionGroup(rootKey, record, maxActionsPerGroup);
    return group ? [group] : [];
  }

  return Object.entries(record)
    .map(([key, entry]) => parseActionGroup(key, entry, maxActionsPerGroup))
    .filter((group): group is CrmContactActionGroup => Boolean(group));
}

function parseActionGroup(
  fallbackKey: string,
  value: unknown,
  maxActionsPerGroup: number,
): CrmContactActionGroup | null {
  const record = asRecord(value);
  if (!record) return null;

  const key = readString(record.key) ?? fallbackKey;
  if (isPrivateKey(key)) return null;

  const label = readString(record.label)
    ?? readString(record.title)
    ?? formatLabel(key);
  const description = readString(record.description) ?? readString(record.summary);
  const rawActions = record.actions ?? record.links ?? record.buttons ?? record.items ?? null;
  const actions = parseActions(rawActions ?? record, maxActionsPerGroup);

  return {
    key,
    label,
    description,
    source: "configured",
    actions,
  };
}

function parseConfiguredChannels(rootKey: string, value: unknown): CrmContactAction[] {
  if (Array.isArray(value)) {
    return value
      .map((entry, index) => parseContactChannel(`${rootKey}.${index}`, entry))
      .filter((action): action is CrmContactAction => Boolean(action));
  }

  const record = asRecord(value);
  if (!record) return [];

  const nestedChannels = record.channels ?? record.items ?? record.links ?? record.profiles ?? record.socials;
  if (nestedChannels) return parseConfiguredChannels(rootKey, nestedChannels);

  if (hasChannelShape(record) && !isChannelCollectionRoot(rootKey, record)) {
    const action = parseContactChannel(rootKey, record);
    return action ? [action] : [];
  }

  return Object.entries(record)
    .map(([key, entry]) => parseContactChannel(key, entry))
    .filter((action): action is CrmContactAction => Boolean(action));
}

function parseContactChannel(fallbackKey: string, value: unknown): CrmContactAction | null {
  if (isPrivateKey(fallbackKey)) return null;

  const record = asRecord(value);
  const key = record ? readString(record.key) ?? fallbackKey : fallbackKey;
  if (isPrivateKey(key)) return null;

  const kind = normalizeChannelKind(
    record
      ? readString(record.kind) ?? readString(record.type) ?? readString(record.channel) ?? fallbackKey
      : fallbackKey,
  );
  const explicitHref = record
    ? readString(record.href) ?? readString(record.url) ?? readString(record.to)
    : readString(value);
  const target = record
    ? readString(record.value)
      ?? readString(record.handle)
      ?? readString(record.username)
      ?? readString(record.phone)
      ?? readString(record.email)
      ?? readString(record.address)
      ?? explicitHref
    : readString(value);
  const href = normalizeChannelHref(kind, explicitHref, target);
  if (!href) return null;

  const label = record
    ? readString(record.label) ?? readString(record.title) ?? defaultChannelLabel(kind, key)
    : defaultChannelLabel(kind, key);
  const description = record ? readString(record.description) ?? readString(record.helpText) : null;
  const disabled = record?.disabled === true;
  const disabledReason = disabled && record
    ? readString(record.disabledReason) ?? readString(record.reason)
    : null;

  return {
    key,
    label,
    description,
    href,
    tone: normalizeChannelTone(kind, record ? readString(record.tone) ?? readString(record.status) : null),
    disabled,
    disabledReason,
  };
}

function parseActions(value: unknown, maxActions: number): CrmContactAction[] {
  if (Array.isArray(value)) {
    return value
      .map((entry, index) => parseAction(`action-${index + 1}`, entry))
      .filter((action): action is CrmContactAction => Boolean(action))
      .slice(0, maxActions);
  }

  const record = asRecord(value);
  if (!record) return [];

  const ignoredGroupKeys = new Set(["key", "label", "title", "description", "summary", "actions", "links", "buttons", "items"]);
  return Object.entries(record)
    .filter(([key]) => !ignoredGroupKeys.has(key))
    .map(([key, entry]) => parseAction(key, entry))
    .filter((action): action is CrmContactAction => Boolean(action))
    .slice(0, maxActions);
}

function parseAction(fallbackKey: string, value: unknown): CrmContactAction | null {
  if (isPrivateKey(fallbackKey)) return null;

  const record = asRecord(value);
  if (!record) return null;

  const key = readString(record.key) ?? fallbackKey;
  if (isPrivateKey(key)) return null;

  const href = normalizeCrmActionHref(readString(record.href) ?? readString(record.url) ?? readString(record.to));
  if (!href) return null;

  const label = readString(record.label)
    ?? readString(record.title)
    ?? readString(record.actionLabel)
    ?? formatLabel(key);
  const description = readString(record.description) ?? readString(record.helpText);
  const disabled = record.disabled === true;
  const disabledReason = disabled
    ? readString(record.disabledReason) ?? readString(record.reason)
    : null;

  return {
    key,
    label,
    description,
    href,
    tone: normalizeTone(readString(record.tone) ?? readString(record.status)),
    disabled,
    disabledReason,
  };
}

function hasGroupShape(record: Record<string, unknown>): boolean {
  return ["label", "title", "description", "summary", "actions", "links", "buttons", "items"].some((key) => key in record);
}

function hasChannelShape(record: Record<string, unknown>): boolean {
  return ["kind", "type", "channel", "href", "url", "to", "value", "handle", "username", "phone", "email", "address"].some((key) => key in record);
}

function isChannelCollectionRoot(rootKey: string, record: Record<string, unknown>): boolean {
  const normalizedRootKey = rootKey.toLowerCase().replace(/[\s_-]+/g, "");
  if (normalizedRootKey.includes(".")) return false;
  const looksLikeCollection = normalizedRootKey.endsWith("channels")
    || normalizedRootKey === "socialmedia"
    || normalizedRootKey === "socialprofiles";
  if (!looksLikeCollection) return false;
  return !["kind", "type", "channel", "href", "url", "to", "value", "handle", "username", "address"].some((key) => key in record);
}

function normalizeTone(value: string | null): CrmContactActionTone {
  if (value === "primary" || value === "success" || value === "warning" || value === "danger") return value;
  if (value === "critical" || value === "error" || value === "failed") return "danger";
  if (value === "ready" || value === "passed" || value === "complete") return "success";
  return "neutral";
}

function normalizeChannelTone(kind: CrmContactChannelKind, value: string | null): CrmContactActionTone {
  const configuredTone = normalizeTone(value);
  if (configuredTone !== "neutral") return configuredTone;
  if (kind === "email" || kind === "telegram" || kind === "linkedin") return "primary";
  if (kind === "phone" || kind === "sms" || kind === "whatsapp") return "success";
  return "neutral";
}

function normalizeChannelKind(value: string | null): CrmContactChannelKind {
  if (!value) return "other";
  const normalized = value.trim().replace(/[\s_-]+/g, "").toLowerCase();
  return CHANNEL_KIND_ALIASES[normalized] ?? "other";
}

function normalizeChannelHref(
  kind: CrmContactChannelKind,
  explicitHref: string | null,
  target: string | null,
): string | null {
  const explicit = normalizeCrmActionHref(explicitHref);
  if (explicit) return explicit;
  if (!target) return null;

  if (kind === "email") return normalizeCrmActionHref(`mailto:${target}`);
  if (kind === "phone") return normalizeCrmActionHref(`tel:${target}`);
  if (kind === "sms") return normalizeCrmActionHref(`sms:${target}`);
  if (kind === "whatsapp") return normalizeWhatsAppHref(target);
  if (kind === "telegram") return normalizeTelegramHref(target);
  if (kind === "instagram") return normalizeSocialProfileHref(target, "https://instagram.com/");
  if (kind === "facebook") return normalizeSocialProfileHref(target, "https://facebook.com/");
  if (kind === "x") return normalizeSocialProfileHref(target, "https://x.com/");
  if (kind === "linkedin" || kind === "website") return normalizeCrmActionHref(target);
  return normalizeCrmActionHref(target);
}

function normalizeWhatsAppHref(value: string): string | null {
  const explicit = normalizeCrmActionHref(value);
  if (explicit) return explicit;

  const digits = value.replace(/\D/g, "");
  if (digits.length < 5 || digits.length > 20) return null;
  return normalizeCrmActionHref(`https://wa.me/${digits}`);
}

function normalizeTelegramHref(value: string): string | null {
  const explicit = normalizeCrmActionHref(value);
  if (explicit) return explicit;

  const handle = value.trim().replace(/^@/, "");
  if (!TELEGRAM_HANDLE_RE.test(handle)) return null;
  return normalizeCrmActionHref(`https://t.me/${handle}`);
}

function normalizeSocialProfileHref(value: string, baseUrl: string): string | null {
  const explicit = normalizeCrmActionHref(value);
  if (explicit) return explicit;

  const handle = value.trim().replace(/^@/, "");
  if (!SOCIAL_HANDLE_RE.test(handle)) return null;
  return normalizeCrmActionHref(`${baseUrl}${handle}`);
}

function defaultChannelLabel(kind: CrmContactChannelKind, key: string): string {
  if (kind === "email") return "Email contact";
  if (kind === "phone") return "Call contact";
  if (kind === "sms") return "Text contact";
  if (kind === "whatsapp") return "WhatsApp";
  if (kind === "telegram") return "Telegram";
  if (kind === "linkedin") return "LinkedIn";
  if (kind === "website") return "Website";
  if (kind === "instagram") return "Instagram";
  if (kind === "facebook") return "Facebook";
  if (kind === "x") return "X";
  return formatLabel(key);
}

function uniqueActions(actions: CrmContactAction[]): CrmContactAction[] {
  const seen = new Set<string>();
  return actions.filter((action) => {
    if (seen.has(action.href)) return false;
    seen.add(action.href);
    return true;
  });
}

function normalizePositiveInteger(value: number | undefined, fallback: number): number {
  return typeof value === "number" && Number.isInteger(value) && value > 0 ? value : fallback;
}

function readString(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function isPrivateKey(key: string): boolean {
  return PRIVATE_KEY_PATTERN.test(key);
}

function formatLabel(value: string): string {
  return value
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim()
    .replace(/\s+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}
